/**
 * MONTHLY CLOSING PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Around the 5th of each month, close the previous month:
 * - Calculate all income, expenses, advances
 * - Distribute savings pool equally
 * - Track Wise withdrawal fees
 * - Most partners take full balance → balance = 0
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CalendarCheck,
  Lock,
  Unlock,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  PiggyBank,
  Wallet,
  Check,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Select } from '../../components/ui/Select'
import { Input } from '../../components/ui/Input'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { SkeletonStatCard, SkeletonCard } from '../../components/ui/Skeleton'
import { apiClient } from '../../lib/api'

// Animation configuration
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

interface MonthSummary {
  year: number
  month: string
  income: {
    usd: number
    pkr: number
    details: any[]
  }
  expenses: {
    pkr: number
    details: any[]
  }
  advances: any[]
  withdrawals: {
    total_usd: number
    total_pkr_received: number
    total_fees_usd: number
    total_fees_pkr: number
    count: number
  }
  distributions: any[]
  reserves: any[]
  savings_pool: {
    percentage: number
    usd: number
    pkr: number
  }
  net_profit: {
    usd: number
    pkr: number
  }
}

interface MonthlyClosing {
  id: number
  year: number
  month: string
  closing_date: string | null
  total_income_usd: number
  total_income_pkr: number
  total_expenses_pkr: number
  total_advances_pkr: number
  total_withdrawals_usd: number
  total_wise_fees: number
  savings_pool_usd: number
  savings_pool_pkr: number
  reserves_usd: number
  reserves_pkr: number
  status: 'open' | 'closed'
  closed_at: string | null
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

export function MonthlyClosingPage() {
  const [loading, setLoading] = useState(true)
  const [closings, setClosings] = useState<MonthlyClosing[]>([])
  const [summary, setSummary] = useState<MonthSummary | null>(null)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth() === 0 ? 11 : new Date().getMonth() - 1])
  const [error, setError] = useState<string | null>(null)

  // Modal states
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [closeSuccess, setCloseSuccess] = useState(false)
  const [closingDate, setClosingDate] = useState(new Date().toISOString().split('T')[0])

  // Expanded sections
  const [expandedSection, setExpandedSection] = useState<string | null>('distributions')

  useEffect(() => {
    fetchClosings()
  }, [selectedYear])

  useEffect(() => {
    fetchSummary()
  }, [selectedYear, selectedMonth])

  const fetchClosings = async () => {
    try {
      const response = await apiClient.get(`/monthly-closing?year=${selectedYear}`)
      if (response.success) {
        setClosings(response.data || [])
      }
    } catch (err) {
      console.error('Failed to fetch closings:', err)
    }
  }

  const fetchSummary = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await apiClient.get(`/monthly-closing/summary/${selectedYear}/${selectedMonth}`)
      if (response.success) {
        setSummary(response.data)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load summary')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseMonth = async () => {
    try {
      setIsSubmitting(true)
      setError(null)

      const response = await apiClient.post('/monthly-closing/close', {
        year: selectedYear,
        month: selectedMonth,
        closing_date: closingDate
      })

      if (response.success) {
        setCloseSuccess(true)
        await fetchClosings()
        await fetchSummary()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to close month')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'PKR') {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const currentMonthStatus = closings.find(c => c.month === selectedMonth)

  // Loading state
  if (loading && !summary) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
            <div className="h-4 w-72 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-32 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
            <div className="h-10 w-24 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
          </div>
        </div>
        <SkeletonCard className="h-32" />
        <StatCardGrid columns={4}>
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </StatCardGrid>
        <SkeletonCard className="h-64" />
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
            Monthly Closing
          </h1>
          <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mt-1">
            End-of-month financial process • Usually around the 5th
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={selectedMonth}
            onChange={setSelectedMonth}
            options={MONTHS.map(m => ({ value: m, label: m }))}
          />
          <Select
            value={selectedYear.toString()}
            onChange={(value) => setSelectedYear(parseInt(value))}
            options={[
              { value: '2024', label: '2024' },
              { value: '2025', label: '2025' },
              { value: '2026', label: '2026' }
            ]}
          />
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={() => {
              fetchClosings()
              fetchSummary()
            }}
          >
            Refresh
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="mb-6"
          >
            <GlassCard className="border border-red/20 bg-red/5">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-xl bg-red/10 flex items-center justify-center">
                  <CalendarCheck className="w-5 h-5 text-red" />
                </div>
                <div className="flex-1">
                  <p className="text-red font-medium">{error}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Month Status Banner */}
      <motion.div variants={itemVariants} className="mb-6">
        <motion.div
          whileHover={{ scale: 1.005 }}
          className={`relative overflow-hidden rounded-2xl p-5 ${
            currentMonthStatus?.status === 'closed'
              ? 'bg-gradient-to-r from-green to-emerald-500'
              : 'bg-gradient-to-r from-orange to-amber-500'
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                {currentMonthStatus?.status === 'closed' ? (
                  <Lock className="w-7 h-7 text-white" />
                ) : (
                  <Unlock className="w-7 h-7 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-title2 font-bold text-white mb-0.5">
                  {selectedMonth} {selectedYear}
                </h2>
                <p className="text-white/80 text-subheadline">
                  {currentMonthStatus?.status === 'closed'
                    ? `Closed on ${new Date(currentMonthStatus.closed_at!).toLocaleDateString()}`
                    : 'Month is open • Ready to close'}
                </p>
              </div>
            </div>
            {currentMonthStatus?.status !== 'closed' && (
              <Button
                variant="secondary"
                icon={<CalendarCheck className="w-4 h-4" />}
                onClick={() => {
                  setCloseSuccess(false)
                  setIsCloseModalOpen(true)
                }}
                className="bg-white/20 border-white/30 text-white hover:bg-white/30"
              >
                Close Month
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Summary Cards */}
      {summary && (
        <motion.div variants={itemVariants}>
          <StatCardGrid columns={4}>
            <StatCard
              title="Total Income"
              value={formatCurrency(summary.income.usd, 'USD')}
              subtitle={formatCurrency(summary.income.pkr, 'PKR')}
              icon={TrendingUp}
              color="green"
              badge={<Badge variant="green">Revenue</Badge>}
            />
            <StatCard
              title="Total Expenses"
              value={formatCurrency(summary.expenses.pkr, 'PKR')}
              subtitle={`Wise Fees: ${formatCurrency(summary.withdrawals.total_fees_usd, 'USD')}`}
              icon={TrendingDown}
              color="red"
              badge={<Badge variant="red">Costs</Badge>}
            />
            <StatCard
              title={`Savings Pool (${summary.savings_pool.percentage}%)`}
              value={formatCurrency(summary.savings_pool.usd, 'USD')}
              subtitle={formatCurrency(summary.savings_pool.pkr, 'PKR')}
              icon={PiggyBank}
              color="purple"
              badge={<Badge variant="purple">Equal Split</Badge>}
            />
            <StatCard
              title="Net Distributable"
              value={formatCurrency(summary.net_profit.usd, 'USD')}
              subtitle={formatCurrency(summary.net_profit.pkr, 'PKR')}
              icon={DollarSign}
              color="blue"
              badge={<Badge variant="blue">Profit</Badge>}
            />
          </StatCardGrid>
        </motion.div>
      )}

      {/* Detailed Breakdown */}
      {summary && (
        <motion.div variants={itemVariants} className="mt-6">
          <ContentCard>
            {/* Wise Withdrawals Section */}
            <div className="border-b border-black/[0.04] dark:border-white/[0.04]">
              <motion.button
                whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                onClick={() => setExpandedSection(expandedSection === 'withdrawals' ? null : 'withdrawals')}
                className="w-full p-5 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange/10 to-orange/5 rounded-2xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-orange" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">
                      Wise Withdrawals
                    </h3>
                    <p className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">
                      {summary.withdrawals.count} withdrawals • {formatCurrency(summary.withdrawals.total_usd, 'USD')} converted
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-bold text-body text-orange">
                      Fees: {formatCurrency(summary.withdrawals.total_fees_usd, 'USD')}
                    </div>
                    <div className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">
                      Received: {formatCurrency(summary.withdrawals.total_pkr_received, 'PKR')}
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: expandedSection === 'withdrawals' ? 180 : 0 }}
                    transition={springConfig}
                  >
                    <ChevronDown className="w-5 h-5 text-light-text-quaternary" />
                  </motion.div>
                </div>
              </motion.button>
            </div>

            {/* Partner Distributions Section */}
            <div className="border-b border-black/[0.04] dark:border-white/[0.04]">
              <motion.button
                whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                onClick={() => setExpandedSection(expandedSection === 'distributions' ? null : 'distributions')}
                className="w-full p-5 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue/10 to-blue/5 rounded-2xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">
                      Partner Distributions
                    </h3>
                    <p className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">
                      {summary.distributions?.length || 0} partners • Share-based allocation
                    </p>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: expandedSection === 'distributions' ? 180 : 0 }}
                  transition={springConfig}
                >
                  <ChevronDown className="w-5 h-5 text-light-text-quaternary" />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {expandedSection === 'distributions' && summary.distributions && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={springConfig}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 space-y-3">
                      {summary.distributions.map((dist: any, index: number) => (
                        <motion.div
                          key={dist.partner_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="flex items-center justify-between p-4 bg-gradient-to-br from-black/[0.02] to-transparent dark:from-white/[0.02] rounded-xl"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${
                              index % 5 === 0 ? 'from-blue to-blue/70' :
                              index % 5 === 1 ? 'from-purple to-purple/70' :
                              index % 5 === 2 ? 'from-green to-green/70' :
                              index % 5 === 3 ? 'from-orange to-orange/70' :
                              'from-pink to-pink/70'
                            }`}>
                              <span className="text-white font-bold text-caption1">
                                {dist.partner_name?.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                              </span>
                            </div>
                            <div>
                              <div className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">
                                {dist.partner_name}
                              </div>
                              <div className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                                {dist.share_percentage}% share
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-body text-green">
                              {formatCurrency(dist.ending_balance_usd || 0, 'USD')}
                            </div>
                            <div className="text-footnote text-green/70">
                              {formatCurrency(dist.ending_balance_pkr || 0, 'PKR')}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </ContentCard>
        </motion.div>
      )}

      {/* Close Month Modal */}
      <Modal
        isOpen={isCloseModalOpen}
        onClose={() => {
          setIsCloseModalOpen(false)
          setCloseSuccess(false)
        }}
        title={closeSuccess ? 'Month Closed Successfully!' : `Close ${selectedMonth} ${selectedYear}`}
        size="md"
      >
        {closeSuccess ? (
          <div className="text-center py-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 15, stiffness: 300 }}
              className="w-20 h-20 bg-gradient-to-br from-green/20 to-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <Sparkles className="w-10 h-10 text-green" />
            </motion.div>
            <h3 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
              Month Closed!
            </h3>
            <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mb-6">
              All calculations complete. Savings distributed equally to partners.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setIsCloseModalOpen(false)
                setCloseSuccess(false)
              }}
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
              This will finalize all calculations for {selectedMonth} {selectedYear} and distribute the savings pool equally among all active partners.
            </p>

            {summary && (
              <div className="p-4 bg-gradient-to-br from-purple/5 to-pink/5 rounded-2xl space-y-3">
                <div className="flex justify-between text-footnote">
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">Total Income</span>
                  <span className="font-semibold text-green">{formatCurrency(summary.income.usd, 'USD')} + {formatCurrency(summary.income.pkr, 'PKR')}</span>
                </div>
                <div className="flex justify-between text-footnote">
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">Total Expenses</span>
                  <span className="font-semibold text-red">{formatCurrency(summary.expenses.pkr, 'PKR')}</span>
                </div>
                <div className="flex justify-between text-footnote">
                  <span className="text-light-text-secondary dark:text-dark-text-secondary">Wise Fees</span>
                  <span className="font-semibold text-orange">{formatCurrency(summary.withdrawals.total_fees_usd, 'USD')}</span>
                </div>
                <div className="border-t border-black/[0.06] dark:border-white/[0.06] pt-3 flex justify-between">
                  <span className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">Savings Pool ({summary.savings_pool.percentage}%)</span>
                  <span className="font-bold text-body text-purple">{formatCurrency(summary.savings_pool.usd, 'USD')} + {formatCurrency(summary.savings_pool.pkr, 'PKR')}</span>
                </div>
              </div>
            )}

            <Input
              label="Closing Date"
              type="date"
              value={closingDate}
              onChange={(e) => setClosingDate(e.target.value)}
            />

            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setIsCloseModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                fullWidth
                icon={<Lock className="w-4 h-4" />}
                onClick={handleCloseMonth}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Close Month
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  )
}
